import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { isOwner, canManageEmployees } from "@/lib/perms";
import { useOrg } from "@/components/org/useOrg";
import { EmployeesTab } from "@/components/org/EmployeesTab";
import { ProfileTab, DepartmentsTab, StructureTab, PermissionsTab, BranchesTab, PartnersTab, ActivityTab } from "@/components/org/OrgTabs";
import { OrderScheduleTab } from "@/components/org/OrderScheduleTab";
import { Sparkles } from "lucide-react";

const OWNER_TABS = [
  ["profile", "Organisation Profile"], ["departments", "Departments"], ["employees", "Employees & Roles"], ["structure", "Reporting Structure"],
  ["partners", "Business Partners"], ["permissions", "Permissions & Access"], ["branches", "Branches / Locations"], ["schedule", "Order Schedule"], ["activity", "Activity Logs"],
];
const MANAGER_TABS = [["employees", "Employees & Roles"], ["structure", "Reporting Structure"]];

export default function Organization() {
  const { user, business } = useAuth();
  const navigate = useNavigate();
  const allowed = canManageEmployees(user);
  const { data, reload } = useOrg(allowed);
  const owner = isOwner(user);
  if (!allowed) return <p className="text-stone-500" data-testid="org-no-access">You don't have access to organisation management.</p>;
  if (!data) return <Loader />;
  const tabs = owner ? OWNER_TABS : MANAGER_TABS;
  const setupOpen = owner && data.setup.status !== "completed";
  const panels = {
    profile: <ProfileTab />, departments: <DepartmentsTab data={data} reload={reload} />, employees: <EmployeesTab data={data} reload={reload} />,
    structure: <StructureTab data={data} />, partners: <PartnersTab />, permissions: <PermissionsTab data={data} reload={reload} />,
    branches: <BranchesTab data={data} reload={reload} />, activity: <ActivityTab data={data} />, schedule: <OrderScheduleTab />,
  };
  return (
    <div className="space-y-6" data-testid="organization-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-stone-900 sm:text-3xl">Organisation</h1>
        <p className="mt-1 text-stone-500">{owner ? `Manage ${business?.name}'s team, departments and access.` : `Manage your department's team.`}</p>
      </div>
      {setupOpen && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#0D5C3A]/20 bg-[#F0ECE1] p-4" data-testid="org-setup-reminder">
          <p className="flex items-center gap-2 text-sm font-medium text-[#0D5C3A]"><Sparkles className="h-4 w-4" /> Your organisation setup isn't finished yet.</p>
          <Button size="sm" onClick={() => navigate("/app/setup")} data-testid="org-resume-setup-btn" className="bg-[#0D5C3A] hover:bg-[#084229]">Resume setup</Button>
        </div>
      )}
      <Tabs defaultValue={owner ? "profile" : "employees"}>
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-white p-1">
          {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`org-tab-${k}`} className="data-[state=active]:bg-[#0D5C3A] data-[state=active]:text-white">{l}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([k]) => <TabsContent key={k} value={k} className="mt-5">{panels[k]}</TabsContent>)}
      </Tabs>
    </div>
  );
}
